import React, { useId, useState } from "react";
import { Dialog } from "../ui/Dialog";
import { X, ChevronDown } from "lucide-react";

interface AddTagDialogProps {
  newTagName: string;
  setNewTagName: (name: string) => void;
  newTagColor: string;
  setNewTagColor: (color: string) => void;
  onClose: () => void;
  onAdd: () => void;
}

const TAG_COLORS = [
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#eab308", // yellow
  "#84cc16", // lime
  "#22c55e", // green
  "#10b981", // emerald
  "#14b8a6", // teal
  "#06b6d4", // cyan
  "#0ea5e9", // sky
  "#3b82f6", // blue
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#a855f7", // purple
  "#d946ef", // fuchsia
  "#ec4899", // pink
  "#f43f5e", // rose
  "#64748b", // slate
];

const AddTagDialog: React.FC<AddTagDialogProps> = ({
  newTagName,
  setNewTagName,
  newTagColor,
  setNewTagColor,
  onClose,
  onAdd,
}) => {
  const titleId = useId();
  const [showColorPicker, setShowColorPicker] = useState(false);

  return (
    <Dialog
      onClose={onClose}
      labelledBy={titleId}
      backdropClassName="z-40"
      panelClassName="w-full max-w-md max-h-[95vh] overflow-y-auto"
      closeOnBackdropClick={false}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 id={titleId} className="text-lg font-semibold text-warm-text-primary">Add New Tag</h3>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-warm-text-secondary hover:text-warm-text-primary transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-warm-text-secondary mb-1">
            Color
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="w-full bg-warm-bg-elevated border border-warm-border rounded-lg px-3 py-2 text-center focus:outline-none focus:ring-2 focus:ring-warm-blue flex items-center justify-center gap-2"
            >
              <span
                className="w-6 h-6 rounded-full"
                style={{ backgroundColor: newTagColor }}
              />
              <ChevronDown size={16} className="text-warm-text-tertiary" />
            </button>
            {showColorPicker && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-warm-bg-card border border-warm-border rounded-lg p-2 z-10 shadow-lg">
                <div className="grid grid-cols-6 gap-2">
                  {TAG_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => {
                        setNewTagColor(color);
                        setShowColorPicker(false);
                      }}
                      className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                        newTagColor === color ? "ring-2 ring-warm-text-primary ring-offset-2 ring-offset-warm-bg-card" : ""
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-warm-border">
                  <input
                    type="color"
                    value={newTagColor}
                    onChange={(e) => setNewTagColor(e.target.value)}
                    className="w-full h-8 rounded cursor-pointer"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-warm-text-secondary mb-1">
            Tag Name
          </label>
          <input
            type="text"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && onAdd()}
            placeholder="e.g., Urgent, Important, Review"
            className="w-full bg-warm-bg-elevated border border-warm-border rounded-lg px-3 py-2 text-sm text-warm-text-primary placeholder:text-warm-text-tertiary focus:outline-none focus:ring-2 focus:ring-warm-blue"
            autoFocus
          />
        </div>

        {/* Preview */}
        {newTagName && (
          <div>
            <label className="block text-sm font-medium text-warm-text-secondary mb-1">
              Preview
            </label>
            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: newTagColor }}
            >
              {newTagName}
            </span>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-warm-bg-elevated hover:bg-warm-border-subtle text-warm-text-primary rounded-lg text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onAdd}
            className="flex-1 px-4 py-2 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-lg text-sm transition-colors"
          >
            Add Tag
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default AddTagDialog;
